import { z } from 'zod';
/**
 * ehr#98 - 12-hour / 24-hour time display (TIME_FORMAT_SETTING_SPEC_09-18-V1).
 *
 * Two independent settings, both defaulting to 12h so nothing changes until someone switches:
 * - `organizations.clientTimeFormat`: every time a client sees (confirmations, reminders,
 *   invites, client-facing forms and documents). Set in Data & Settings > Practice info.
 * - `users.personalTimeFormat`: one staff member's own view of every internal screen, in
 *   every module. Set in My Preferences.
 *
 * Display only: timestamps stay UTC ISO 8601 everywhere, and time inputs / pickers stay AM/PM.
 */
export const TIME_FORMATS = ['12h', '24h'];
export const timeFormatSchema = z.enum(TIME_FORMATS);
export const DEFAULT_TIME_FORMAT = '12h';
/** Anything stored or sent → a valid format ('24h' only when it says so). */
export const asTimeFormat = (v) => (v === '24h' ? '24h' : '12h');
/**
 * Intl options for the clock part, to spread into toLocaleString / toLocaleTimeString:
 * 12h "6:30 PM" (no leading zero), 24h "18:30" (zero-padded HH:mm; h23 so midnight is 00:00).
 */
export function clockOptions(fmt) {
    return fmt === '24h'
        ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
        : { hour: 'numeric', minute: '2-digit', hour12: true };
}
/** The time of day of `at` in `fmt` ('' for an invalid date). Device zone unless `timeZone`. */
export function formatClock(at, fmt, timeZone) {
    const d = typeof at === 'string' ? new Date(at) : at;
    if (isNaN(d.getTime()))
        return '';
    return d.toLocaleTimeString('en-US', { ...clockOptions(fmt), ...(timeZone ? { timeZone } : {}) });
}
/** The spec's formatTime(iso, { context }): the one place the format choice is made. */
export function formatTime(iso, opts) {
    const fmt = asTimeFormat(opts.context === 'client' ? opts.clientFmt : opts.personalFmt);
    return formatClock(iso, fmt, opts.timeZone);
}
/**
 * ehr#420 - each person's own theme on every internal screen, in every module (`users.personalTheme`,
 * set on the Provider Profile next to Time format). 'system' follows the device's light / dark setting.
 * Light by default, so nothing changes until someone switches.
 */
export const THEMES = ['light', 'dark', 'system'];
export const themeSchema = z.enum(THEMES);
export const DEFAULT_THEME = 'light';
/** Anything stored or sent → a valid theme (light unless it says dark or system). */
export const asTheme = (v) => (v === 'dark' || v === 'system' ? v : 'light');
/** PUT /me/preferences: either setting, or both; an empty body is refused. */
export const myPreferencesSchema = z
    .object({ personalTimeFormat: timeFormatSchema.optional(), personalTheme: themeSchema.optional() })
    .refine((v) => v.personalTimeFormat !== undefined || v.personalTheme !== undefined, {
    message: 'Send personalTimeFormat or personalTheme',
});
//# sourceMappingURL=time.js.map